import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Firestore role-doc id → role NAME, so a user's `roleIds` can be understood.
 *
 * WHY THIS EXISTS: `hasRoleNamed` looks like it unions four fields, but for anyone
 * other than the signed-in user it silently degenerates to the legacy `role` field:
 *
 *   - `roleNames` is NEVER persisted. AuthProvider derives it in memory, for the
 *     current user only. On all 150 production user documents it is absent.
 *   - `roleIds` holds Firestore DOC IDS ('9cmfXjv5EsbFubVuRHUW'), not names, so
 *     matching the string 'student' against it never hits.
 *
 * So Shady Tarek — role 'coach', roleIds [Student, Coach], 4,570 XP, the academy's
 * most active poster — was not a student to `isActiveStudent()`, and vanished from
 * the Mark Attendance roster. He could not be marked present, so he earned no XP, no
 * streak, and no class credit was consumed against his subscription.
 *
 * Fixing it here rather than at each call site means every existing consumer of
 * roleChecks (31 files) is corrected without being touched.
 *
 * Same shape as roleConfig.ts: a synchronous accessor that returns whatever is
 * cached and kicks off a background refresh, so the many sync callers
 * (isActiveStudent, isCoach, …) stay sync. Before the first load resolves, behaviour
 * is exactly what it is today — the legacy `role` field — so this can only ever add
 * matches, never remove one.
 */

let idToName: Record<string, string> = {};
let fetchPromise: Promise<void> | null = null;

async function load(): Promise<void> {
    try {
        const snap = await getDocs(collection(db, 'roles'));
        const next: Record<string, string> = {};
        snap.forEach(d => {
            const name = (d.data() as { name?: unknown }).name;
            if (typeof name === 'string' && name.trim()) next[d.id] = name.trim().toLowerCase();
        });
        idToName = next;
    } catch (err) {
        // Never throw into a role check: a failed load degrades to today's behaviour.
        console.warn('[roleNameMap] could not load roles; role-id resolution unavailable', err);
    }
}

/** The cached map. Kicks off a one-time background load on first call. */
export function getRoleNameMap(): Record<string, string> {
    if (!fetchPromise) fetchPromise = load();
    return idToName;
}

/** Awaitable variant, for callers that can wait (tests, AuthProvider bootstrap). */
export async function initRoleNameMap(): Promise<Record<string, string>> {
    if (!fetchPromise) fetchPromise = load();
    await fetchPromise;
    return idToName;
}

/**
 * Seed the map from an already-loaded roles list — AuthProvider keeps a live
 * onSnapshot cache (Task #16), so it can hand it over instead of a second read.
 */
export function primeRoleNameMap(roles: { id: string; name?: string }[]): void {
    const next: Record<string, string> = {};
    for (const r of roles) {
        if (r && typeof r.name === 'string' && r.name.trim()) next[r.id] = r.name.trim().toLowerCase();
    }
    if (Object.keys(next).length > 0) {
        idToName = next;
        fetchPromise = fetchPromise ?? Promise.resolve();
    }
}

export function __resetRoleNameMapForTests(): void {
    idToName = {};
    fetchPromise = null;
}
